import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DateRangeModal } from '@/components/ui/DateRangeModal'
import { MobileFloatingFilter } from '@/components/ui/MobileFloatingFilter'
import { Skeleton } from '@/components/ui/skeleton'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts'
import {
  FileText,
  DollarSign,
  ShoppingCart,
  TrendingUp,
  Filter,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Search,
  Building2
} from 'lucide-react'
import { useCurrency } from '@/hooks/useCurrency'
import { parseDate, createDateRange } from '@/lib/dateUtils'
import { useBusinessDayDefaultRange } from '@/hooks/useBusinessDayDefaultRange'
import { ApiService } from '@/services/api'
import type { PaginationInfo, BillsSummary } from '@/types'
import { getBillStatusVariant, getBillStatusLabel } from '@/lib/billStatus'

const PAGE_SIZE = 25
const SKELETON_ROW_COUNT = 8

interface CombinedBill {
  _id: string
  billNumber?: string
  billSubmittedDate?: string
  createdAt: string
  customerName?: string
  total?: number
  status?: string
  paymentStatus?: string
  restaurantId: string
  restaurantName: string
}

interface RestaurantSalesBreakdown {
  restaurantId: string
  name: string
  totalRevenue: number
  totalOrders: number
  completedOrders: number
  averageOrderValue: number
  notSettledCount: number
  notSettledAmount: number
}

// Stable, distinct color per restaurant — kept in sync with CombinedDashboardScreen's palette.
const RESTAURANT_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#f43f5e', '#14b8a6', '#f97316', '#84cc16']
const colorForIndex = (i: number) => RESTAURANT_COLORS[i % RESTAURANT_COLORS.length]

const CustomTooltip = ({ active, payload, label }: any) => {
  const { formatCurrency } = useCurrency()
  if (!active || !payload || !payload.length) return null
  return (
    <div className="rounded-lg border bg-background px-3 py-2 shadow-sm text-xs">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((entry: any, i: number) => (
        <p key={i} style={{ color: entry.color }}>
          {entry.name}: {entry.name?.toLowerCase().includes('order') ? entry.value : formatCurrency(entry.value)}
        </p>
      ))}
    </div>
  )
}

const EMPTY_SUMMARY: BillsSummary = {
  totalRevenue: 0,
  totalOrders: 0,
  averageOrderValue: 0,
  completedOrders: 0,
  notSettledCount: 0,
  notSettledAmount: 0
}

export default function CombinedSalesReportScreen() {
  const { formatCurrency, currencySymbol } = useCurrency()
  const [bills, setBills] = useState<CombinedBill[]>([])
  const [summary, setSummary] = useState<BillsSummary>(EMPTY_SUMMARY)
  const [byRestaurant, setByRestaurant] = useState<RestaurantSalesBreakdown[]>([])
  const [pagination, setPagination] = useState<PaginationInfo>({ page: 1, pageSize: PAGE_SIZE, total: 0, totalPages: 1 })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [dateRange, setDateRange] = useBusinessDayDefaultRange()

  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(searchInput), 400)
    return () => clearTimeout(timer)
  }, [searchInput])

  const loadBills = async (targetPage: number = pagination.page) => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await ApiService.getCombinedBills(dateRange, {
        page: targetPage,
        pageSize: PAGE_SIZE,
        status: statusFilter,
        search: searchTerm || undefined
      })
      if (response.success) {
        setBills(response.data)
        setSummary(response.summary || EMPTY_SUMMARY)
        const sortedBreakdown = [...(response.byRestaurant || [])].sort(
          (a: RestaurantSalesBreakdown, b: RestaurantSalesBreakdown) => (b.totalRevenue || 0) - (a.totalRevenue || 0)
        )
        setByRestaurant(sortedBreakdown)
        setPagination(response.pagination || { page: 1, pageSize: PAGE_SIZE, total: response.data.length, totalPages: 1 })
      } else {
        setError(response.message || 'Failed to load combined sales report')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load combined sales report')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadBills(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter, dateRange])

  const handleDateChange = (field: 'from' | 'to', value: string) => {
    const newDate = parseDate(value)
    setDateRange(prev =>
      field === 'from' ? createDateRange(newDate, prev.to) : createDateRange(prev.from, newDate)
    )
  }

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return 'N/A'
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true
    })
  }

  const goToPage = (newPage: number) => {
    if (newPage < 1 || newPage > pagination.totalPages || newPage === pagination.page) return
    loadBills(newPage)
  }

  if (isLoading && bills.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center justify-center py-12 space-y-3">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="text-sm text-muted-foreground">Loading combined sales report...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Unable to load combined sales report</AlertTitle>
          <AlertDescription>
            <p className="mb-3">{error}</p>
            <Button variant="outline" size="sm" className="py-[10px]" onClick={() => loadBills(1)}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold truncate">Combined Sales Report</h1>
          <p className="text-muted-foreground text-sm sm:text-base truncate">Sales data across all your restaurants</p>
        </div>
        <Button onClick={() => loadBills(pagination.page)} variant="outline" size="icon" className="py-[10px] sm:w-auto sm:h-auto sm:px-4 flex-shrink-0">
          <RefreshCw className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      {/* Date Range and Filters - Desktop Only */}
      <Card className="hidden md:block">
        <CardHeader>
          <CardTitle className="text-base flex items-center space-x-2">
            <Filter className="h-4 w-4" />
            <span>Filters</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 sm:gap-4 grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2 lg:col-span-2">
              <Label>Date Range</Label>
              <DateRangeModal
                dateRange={dateRange}
                onDateChange={handleDateChange}
                onApply={() => loadBills(1)}
                title="Combined Sales Report Date Range"
                description="Select the date range for your combined sales report"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Bill number, customer..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mobile Search & Filter */}
      <div className="md:hidden space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search bill number, customer..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger>
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Statistics */}
      <div className="grid gap-2 sm:gap-4 grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{formatCurrency(summary.totalRevenue)}</div>
            <p className="text-xs text-muted-foreground">From {pagination.total} orders</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{summary.totalOrders}</div>
            <p className="text-xs text-muted-foreground">{summary.completedOrders} completed</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Order Value</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{formatCurrency(summary.averageOrderValue)}</div>
            <p className="text-xs text-muted-foreground">Per order average</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Unsettled</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{formatCurrency(summary.notSettledAmount)}</div>
            <p className="text-xs text-muted-foreground">{summary.notSettledCount} unsettled bills</p>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Comparison */}
      {byRestaurant.length > 0 && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Revenue by Restaurant</CardTitle>
              <CardDescription>Selected period, ranked</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={Math.max(180, byRestaurant.length * 45)}>
                <BarChart data={byRestaurant} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }} barCategoryGap="30%">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${currencySymbol}${(v / 1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={90} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="totalRevenue" name="Revenue" radius={[0, 4, 4, 0]}>
                    {byRestaurant.map((r, i) => (
                      <Cell key={r.restaurantId} fill={colorForIndex(i)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Orders by Restaurant</CardTitle>
              <CardDescription>Selected period, ranked</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={Math.max(180, byRestaurant.length * 45)}>
                <BarChart data={byRestaurant} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }} barCategoryGap="30%">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={90} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="totalOrders" name="Orders" radius={[0, 4, 4, 0]}>
                    {byRestaurant.map((r, i) => (
                      <Cell key={r.restaurantId} fill={colorForIndex(i)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Combined Sales Data Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>Combined Sales Data</span>
          </CardTitle>
          <CardDescription>Bills merged across every restaurant you own</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Desktop Table */}
          <div className="hidden lg:block">
            <div className="max-h-[600px] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background">
                  <TableRow>
                    <TableHead>Bill #</TableHead>
                    <TableHead>Restaurant</TableHead>
                    <TableHead>Date &amp; Time</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                      <TableRow key={`skeleton-${i}`}>
                        {Array.from({ length: 6 }).map((__, j) => (
                          <TableCell key={j}><Skeleton className="h-4 w-full max-w-24" /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : bills.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <div className="flex flex-col items-center space-y-2">
                          <FileText className="h-8 w-8 text-muted-foreground" />
                          <span className="text-muted-foreground">No sales data found</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    bills.map((bill) => (
                      <TableRow key={`${bill.restaurantId}_${bill._id}`}>
                        <TableCell className="font-medium">{bill.billNumber || `#${bill._id.slice(-6)}`}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{bill.restaurantName}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">{formatDateTime(bill.billSubmittedDate || bill.createdAt)}</TableCell>
                        <TableCell>{bill.customerName || 'Guest'}</TableCell>
                        <TableCell className="font-semibold">{formatCurrency(bill.total || 0)}</TableCell>
                        <TableCell>
                          <Badge
                            variant={getBillStatusVariant(bill)}
                          >
                            {getBillStatusLabel(bill)}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-3">
            {bills.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">No sales data found</div>
            ) : (
              bills.map((bill) => (
                <div key={`${bill.restaurantId}_${bill._id}`} className="border rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{bill.billNumber || `#${bill._id.slice(-6)}`}</span>
                    <Badge variant="outline" className="text-xs">{bill.restaurantName}</Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Customer:</span>
                    <span>{bill.customerName || 'Guest'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Date:</span>
                    <span className="text-xs">{formatDateTime(bill.billSubmittedDate || bill.createdAt)}</span>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t">
                    <span className="font-semibold text-lg">{formatCurrency(bill.total || 0)}</span>
                    <Badge
                      variant={getBillStatusVariant(bill)}
                    >
                      {getBillStatusLabel(bill)}
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 mt-4 border-t">
              <span className="text-sm text-muted-foreground">
                Page {pagination.page} of {pagination.totalPages} • {pagination.total} total
              </span>
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm" className="py-[10px]" onClick={() => goToPage(pagination.page - 1)} disabled={pagination.page <= 1 || isLoading}>
                  Prev
                </Button>
                <Button variant="outline" size="sm" className="py-[10px]" onClick={() => goToPage(pagination.page + 1)} disabled={pagination.page >= pagination.totalPages || isLoading}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <MobileFloatingFilter
        dateRange={dateRange}
        onDateChange={handleDateChange}
        onApply={() => loadBills(1)}
        title="Combined Sales Report Date Range"
        description="Select the date range for your combined sales report"
      />
    </div>
  )
}
